/**
 * Session-ended guard for every API call (K25, 2026-10-19).
 *
 * The API's auth guard puts `code: SIGN_IN_REQUIRED` on every 401 it
 * raises: the token is missing, invalid, expired, signed out, or issued
 * before the account's sessions were ended by a password reset or
 * change. On that answer the app wipes its session and shows Sign In.
 *
 * The shared axios client (services/http) handles its own responses, but
 * most screens call the API with plain `fetch`, so the same check is
 * installed here around the global `fetch`. A route's own 401 (an older
 * server refusing an invitation answer) is left to the screen that made
 * the request.
 */
import { resetToLogin } from '../navigationService';
import { createLogger } from '../../utils/logger';
import { wipeSessionOnLogout } from './logoutWipe';

const logger = createLogger('SessionGuard');

/** The code the API's auth guard puts on every 401 it raises. */
export const SIGN_IN_REQUIRED = 'SIGN_IN_REQUIRED';

/**
 * Only the guard's 401s end the session. Servers from before the code was
 * added are recognised by the guard's wording.
 */
export function isSessionOver(body: unknown): boolean {
  if (!body || typeof body !== 'object') return false;
  const { code, message } = body as { code?: unknown; message?: unknown };
  if (code === SIGN_IN_REQUIRED) return true;
  return typeof message === 'string' && /sign in again/i.test(message);
}

let ending: Promise<void> | null = null;

/**
 * Wipe the session once and go to Sign In. Several requests usually fail
 * together when a session ends; they share one wipe.
 */
export function endSessionAndShowSignIn(): Promise<void> {
  if (!ending) {
    logger.warn('The server says the session is over; signing out');
    ending = wipeSessionOnLogout({ sessionExpired: true })
      .catch((err) => logger.error('Session wipe failed', err))
      .then(() => {
        resetToLogin();
        ending = null;
      });
  }
  return ending;
}

function requestUrl(input: RequestInfo | URL): string {
  if (typeof input === 'string') return input;
  if (input instanceof URL) return input.href;
  return input.url;
}

/**
 * Wrap the global `fetch` so a SIGN_IN_REQUIRED 401 from the API ends the
 * session wherever it was requested from. Only requests to `apiOrigin`
 * are looked at; everything else passes through untouched. Safe to call
 * once per process.
 */
export function installSessionEndedFetchGuard(apiOrigin: string): void {
  const g = globalThis as { fetch?: typeof fetch; __fkSessionGuard?: boolean };
  if (!g.fetch || g.__fkSessionGuard) return;
  const nativeFetch = g.fetch.bind(globalThis);
  g.__fkSessionGuard = true;

  g.fetch = async (input: RequestInfo | URL, init?: RequestInit): Promise<Response> => {
    const response = await nativeFetch(input, init);
    if (response.status !== 401) return response;
    try {
      if (!requestUrl(input).startsWith(apiOrigin)) return response;
      // Read a copy; the caller still gets the body.
      const body = await response.clone().json().catch(() => null);
      if (isSessionOver(body)) void endSessionAndShowSignIn();
    } catch (err) {
      logger.debug('401 inspection skipped', err);
    }
    return response;
  };
}
